<template>
  <div class="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <!-- Layout horizontal sur desktop -->
    <div class="hidden sm:flex items-center justify-between">
      <!-- Titre -->
      <h2 class="text-lg font-semibold text-gray-900 mr-6">{{ title }}</h2>
      
      <!-- Filtres compacts -->
      <div class="inline-flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        <button
          :class="[
            'px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2',
            activeFilter === 'my' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          ]"
          :aria-label="`Mes ${assetLabel.toLowerCase()}, ${stats.my} éléments`"
          @click="setFilter('my')"
        >
          <span>Mes {{ assetLabel.toLowerCase() }}</span>
          <span 
            :class="[
              'px-1.5 py-0.5 rounded-full text-xs font-semibold min-w-[20px] text-center',
              activeFilter === 'my' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-gray-200 text-gray-600'
            ]"
          >
            {{ stats.my }}
          </span>
        </button>
        
        <button
          :class="[
            'px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2',
            activeFilter === 'accessible' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          ]"
          :aria-label="`Tous les ${assetLabel.toLowerCase()}, ${stats.total} éléments`"
          @click="setFilter('accessible')"
        >
          <span>Tous les {{ assetLabel.toLowerCase() }}</span>
          <span 
            :class="[
              'px-1.5 py-0.5 rounded-full text-xs font-semibold min-w-[20px] text-center',
              activeFilter === 'accessible' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-gray-200 text-gray-600'
            ]"
          >
            {{ stats.total }}
          </span>
        </button>
      </div>


      
      <!-- Bouton refresh -->
      <button 
        :disabled="isLoading" 
        :aria-label="`Rafraîchir la liste des ${assetLabel.toLowerCase()}`"
        class="ml-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
        @click="$emit('refresh')"
      >
        <RefreshCcwIcon 
          :class="[
            'h-4 w-4 transition-transform',
            isLoading ? 'animate-spin' : ''
          ]" 
        />
      </button>
    </div>
    
    <!-- Layout mobile (vertical) -->
    <div class="sm:hidden">
      <!-- Titre mobile -->
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">{{ title }}</h2>
        <button 
          :disabled="isLoading" 
          :aria-label="`Rafraîchir la liste des ${assetLabel.toLowerCase()}`"
          class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          @click="$emit('refresh')"
        >
          <RefreshCcwIcon 
            :class="[
              'h-4 w-4 transition-transform',
              isLoading ? 'animate-spin' : ''
            ]" 
          />
        </button>
      </div>
      
      <!-- Select mobile -->
      <select 
        :value="activeFilter === 'my' ? 'my' : 'accessible'"
        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        @change="handleMobileFilterChange"
      >
        <option value="my">Mes {{ assetLabel.toLowerCase() }} ({{ stats.my }})</option>
        <option value="accessible">Tous les {{ assetLabel.toLowerCase() }} ({{ stats.total }})</option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RefreshCcw as RefreshCcwIcon } from 'lucide-vue-next';

interface AssetFiltersProps {
  title: string;
  assetLabel: string;
  activeFilter: 'my' | 'public' | 'accessible';
  stats: {
    my: number;
    public: number;
    total: number;
  };
  isLoading: boolean;
}

interface AssetFiltersEmits {
  'update:activeFilter': [filter: 'my' | 'public' | 'accessible'];
  'refresh': [];
}

defineProps<AssetFiltersProps>();
const emit = defineEmits<AssetFiltersEmits>();

const setFilter = (filter: 'my' | 'public' | 'accessible'): void => {
  emit('update:activeFilter', filter);
};

const handleMobileFilterChange = (event: Event): void => {
  const target = event.target as HTMLSelectElement;
  const value = target.value as 'my' | 'accessible';
  setFilter(value);
};
</script> 